// src/features/settings/components/product-groups-panel/ProductGroupsGrid.jsx
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Chip, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

export default function ProductGroupsGrid({
  productGroups,
  loading,
  canUpdate,
  canDelete,
  onToggle,
  onDelete,
  processRowUpdate,
  onProcessRowUpdateError,
}) {
  const { t } = useTranslation(['settings', 'common']);

  const CheckboxCell = useCallback(({ row, field }) => (
    <Checkbox
      checked={Boolean(row[field])}
      onChange={() => onToggle(row, field)}
      disabled={!canUpdate || loading}
      color='info'
    />
  ), [onToggle, canUpdate, loading]);

  const verticalHeader = (label) => () => (
    <Box sx={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', width: '100%', fontWeight: 600 }}>
      {label}
    </Box>
  );

  const columns = useMemo(() => [
    {
      field: 'code',
      headerName: t('settings:product_groups.grid.code'),
      width: 90,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" color="info" />
      ),
    },
    {
      field: 'name',
      headerName: t('settings:product_groups.grid.name'),
      flex: 1,
      minWidth: 150,
      editable: canUpdate,
    },
    {
      field: 'description',
      headerName: t('settings:product_groups.grid.description'),
      flex: 1,
      minWidth: 150,
      editable: canUpdate,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'active',
      headerName: t('settings:product_groups.grid.active'),
      width: 90,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.active ? t('common:status.active') : t('common:status.inactive')}
          color={params.row.active ? 'success' : 'default'}
          size="small"
          onClick={canUpdate && !loading ? () => onToggle(params.row, 'active') : undefined}
          sx={canUpdate ? { cursor: 'pointer' } : undefined}
        />
      ),
    },
    {
      field: 'shelf_life_days',
      headerName: t('settings:product_groups.grid.shelf_life_days'),
      width: 120,
      minWidth: 120,
      editable: canUpdate,
      renderCell: (params) => params.value != null
        ? t('settings:product_groups.grid.shelf_life_value', { count: params.value })
        : '-',
    },
    {
      field: 'lot_tracked',
      renderHeader: verticalHeader(t('settings:product_groups.grid.lot_tracked_short')),
      width: 52,
      sortable: false,
      renderCell: (params) => <CheckboxCell row={params.row} field="lot_tracked" />,
    },
    {
      field: 'inventory_tracked',
      renderHeader: verticalHeader(t('settings:product_groups.grid.inventory_tracked_short')),
      width: 52,
      sortable: false,
      renderCell: (params) => <CheckboxCell row={params.row} field="inventory_tracked" />,
    },
    {
      field: 'serial_tracked',
      renderHeader: verticalHeader(t('settings:product_groups.grid.serial_tracked_short')),
      width: 52,
      sortable: false,
      renderCell: (params) => <CheckboxCell row={params.row} field="serial_tracked" />,
    },
    {
      field: 'hazardous',
      renderHeader: verticalHeader(t('settings:product_groups.grid.hazardous_short')),
      width: 52,
      sortable: false,
      renderCell: (params) => <CheckboxCell row={params.row} field="hazardous" />,
    },
    {
      field: 'actions',
      renderHeader: () => (<></>),
      width: 52,
      sortable: false,
      filterable: false,
      renderCell: (params) => canDelete ? (
        <Box>
          <IconButton
            size="small"
            onClick={() => onDelete(params.row)}
            title={t('common:buttons.delete')}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null,
    },
  ], [t, canUpdate, canDelete, loading, onToggle, onDelete]);

  return (
    <DataGrid
      rows={productGroups}
      columns={columns}
      getRowId={(row) => row.product_group_id}
      getRowClassName={(params) => !params.row.active ? 'row-inactive' : ''}
      // loading={loading}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={onProcessRowUpdateError}
      pageSizeOptions={[10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      disableColumnFilter
      disableColumnSorting
      disableColumnMenu
      disableColumnResize
      disableRowSelectionOnClick
      sx={{
        '& .MuiDataGrid-columnHeaders': {
          minHeight: '70px !important',
          maxHeight: '70px !important',
        },
        '& .MuiDataGrid-columnHeader': {
          height: '70px !important',
          alignItems: 'flex-end !important',
          paddingBottom: '8px',
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
          alignItems: 'flex-end',
          justifyContent: 'center',
        },
        '& .row-inactive': {
          opacity: 0.5,
        },
        '& .MuiDataGrid-cell': {
          minHeight: 'unset !important',
          maxHeight: 'none !important',
          height: 'auto !important',
          display: 'flex !important',
          alignItems: 'center !important',
          lineHeight: '1.5 !important',
          padding: '12px 16px !important',
        },
        minHeight: 500,
      }}
    />
  );
}
